import LoginScreen from './LoginScreen';
import HomeScreen from './HomeScreen';
import AdminScreen from './AdminScreen';
import SystemAdminScreen from './SystemAdminScreen';
import TutorialScreen from './TutorialScreen';
import { apiFetch, getAuthToken, clearAuthToken } from './api';
import React, { useState, useEffect } from 'react';
import { ComposedChart, LineChart, Line, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './App.css';

/**
 * Main application component for the psychophysical testing platform.
 * 
 * Manages the state machine between Home, Test, Results, History, and Admin views.
 * Handles patient selection and test initialization parameters (calibration data).
 * 
 * @requirements
 * - REQ-F01: Patient Workflow
 * - REQ-F04: Display Calibration
 * - REQ-U01: Clinical Instructions (UI states)
 */
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!getAuthToken());
  const [view, setView] = useState('home'); // 'home', 'test', 'result', 'history', 'calibrate', 'admin'
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newUserName, setNewUserName] = useState('');
  
  const [testMode, setTestMode] = useState('monitoring');
  const [testModality, setTestModality] = useState('full');
  const [testedEye, setTestedEye] = useState('both');
  
  const [testData, setTestData] = useState(null); 
  const [finalResults, setFinalResults] = useState(null);
  const [overallTime, setOverallTime] = useState(null);
  
  const [calibrationData, setCalibrationData] = useState(null);

  useEffect(() => {
    if (isAuthenticated) fetchUsers();
    const storedCal = localStorage.getItem('visionguard_cal');
    if (storedCal) {
        setCalibrationData(JSON.parse(storedCal));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  /**
   * Fetches the list of all registered patients from the backend API.
   * 
   * If users are found and no user is currently selected, it automatically 
   * selects the first user in the list.
   * 
   * @requirements
   * - REQ-S01: Multi-Tenancy (Patient list retrieval)
   */

  const fetchUsers = async () => {
    try {
      const res = await apiFetch(`/api/patients`);
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
        if (data.length > 0 && !selectedUserId) {
            setSelectedUserId(data[0].id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  };

  /**
   * Creates a new patient record in the database.
   * 
   * Sends the `newUserName` state to the backend and refreshes the user list 
   * upon successful creation.
   * 
   * @requirements
   * - REQ-S01: Multi-Tenancy (Patient creation)
   */

  const createUser = async () => {
    if (!newUserName) return;
    try {
      const res = await apiFetch(`/api/patients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newUserName })
      });
      if (res.ok) {
        const user = await res.json();
        setUsers([...users, user]);
        setSelectedUserId(user.id);
        setNewUserName('');
      }
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * Validates prerequisites before starting a psychophysical test.
   * 
   * Checks if a patient is selected. If display calibration data is missing, 
   * redirects the user to the calibration screen. Otherwise, initiates the test.
   * 
   * @requirements
   * - REQ-F01: Patient Workflow
   * - REQ-F04: Display Calibration
   */

  const attemptStartTest = () => {
      if (!selectedUserId) return alert("Please select or create a user.");
      if (!calibrationData) {
          setView('calibrate');
          return;
      }
      
      const currentUser = users.find(u => u.id === selectedUserId);
      if (currentUser && !currentUser.has_completed_tutorial) {
          setView('tutorial');
      } else {
          startTest(calibrationData);
      }
  };

  const handleTutorialComplete = async () => {
      if (selectedUserId) {
          try {
              await apiFetch(`/api/patients/${selectedUserId}/tutorial_completed`, { method: 'POST' });
              // Update local state so it doesn't trigger again
              setUsers(users.map(u => u.id === selectedUserId ? { ...u, has_completed_tutorial: true } : u));
          } catch (err) {
              console.error("Failed to mark tutorial as completed", err);
          }
      }
      setView('home');
  };

  /**
   * Initializes the psychophysical test session with the backend.
   * 
   * Calculates the maximum safe physical size for the Gabor patch grid based on 
   * the current viewport dimensions. Sends calibration data (px/cm, viewing distance) 
   * to the engine to ensure medically accurate spatial frequencies.
   * 
   * @param {Object} calData - The calibration object.
   * @param {number} calData.pxPerCm - Calibrated CSS pixels per centimeter.
   * @param {number} calData.distance - Expected patient viewing distance in cm.
   * @param {number} calData.devicePixelRatio - The browser's device pixel ratio.
   * 
   * @requirements
   * - REQ-F05: Stimulus Presentation
   * - REQ-F07: Adaptive Test Logic
   */

  const startTest = async (calData) => {
    // Calculate the maximum safe CSS patch size
    // Leave 40px for horizontal margins (20px each side) + 10px gap
    const availableWidth = window.innerWidth - 50; 
    // Leave 300px for headers, subtext, progress bar, and abort button
    const availableHeight = window.innerHeight - 300;
    
    // Max size of the 2x2 grid (must be square)
    const maxGridSize = Math.min(availableWidth, availableHeight, 800);
    // Size of a single patch (minus 5px for half the gap)
    const maxPatchSizePx = Math.floor((maxGridSize / 2) - 5);

    // Target constraint (4 degrees visible -> ~5.71 degrees container for gaussian_0175)
    const targetDegrees = 4.0;
    const patchDegrees = targetDegrees / 0.7; // ~5.71 deg for gaussian_0175
    const patchCm = 2 * calData.viewingDistanceCm * Math.tan((patchDegrees / 2) * (Math.PI / 180));
    const idealPatchPx = patchCm * calData.cssPxPerCm;
    
    const patchCssSize = Math.floor(Math.min(idealPatchPx, maxPatchSizePx));
    
    // Calculate actual achieved target size (may be < 4.0 on small screens)
    const achievedPatchCm = patchCssSize / calData.cssPxPerCm;
    const achievedPatchDegrees = (2 * Math.atan((achievedPatchCm / 2) / calData.viewingDistanceCm)) * (180 / Math.PI);
    const achievedTargetDegrees = achievedPatchDegrees * 0.7;

    try {
      const res = await apiFetch(`/api/csf/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          patient_id: parseInt(selectedUserId),
          test_mode: testMode,
          test_modality: testModality,
          css_px_per_cm: calData.cssPxPerCm,
          viewing_distance_cm: calData.viewingDistanceCm,
          device_pixel_ratio: calData.devicePixelRatio,
          patch_css_size: patchCssSize,
          target_size_degrees: achievedTargetDegrees,
          tested_eye: testedEye,
          user_agent: navigator.userAgent,
          screen_width: window.screen.width,
          screen_height: window.screen.height
        }),
        credentials: 'include' 
      });

      if (!res.ok) throw new Error("Failed to start session");

      const data = await res.json();
      setTestData(data);
      setOverallTime(null);
      setView('test');
    } catch (err) {
      console.error(err);
      alert("Failed to connect to backend.");
    }
  };

  /**
   * Callback fired when the user completes the manual display calibration.
   * 
   * Saves the calibration metrics to local state and localStorage for future 
   * sessions, then immediately starts the test.
   * 
   * @param {Object} data - The computed calibration metrics.
   * 
   * @requirements
   * - REQ-F04: Display Calibration
   */

  const onCalibrationComplete = (data) => {
      setCalibrationData(data);
      startTest(data);
  };

  /**
   * Submits the patient's response for the current trial to the Bayesian engine.
   * 
   * @param {boolean} isCorrect - True if the patient clicked the correct patch.
   * @param {number} cpd - The spatial frequency (cycles per degree) of the target patch.
   * @param {number} reactionTime - The time taken by the patient to respond in seconds.
   * 
   * @returns {Promise<Object>} The next stimulus parameters or final thresholds.
   * 
   * @requirements
   * - REQ-F06: Touch Registration
   * - REQ-F07: Adaptive Test Logic
   */

  const submitAnswer = async (isCorrect, cpd, reactionTime) => {
    try {
      const res = await apiFetch(`/api/csf/next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correct: isCorrect, condition_key: cpd, reaction_time_sec: reactionTime }),
        credentials: 'include' 
      });

      if (res.status === 401) {
         alert("Session expired. Please refresh.");
         return null;
      }

      const data = await res.json();
      return data;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  /**
   * Processes the engine's response and updates the UI state for the next trial.
   * 
   * If the engine flags `test_finished`, it transitions the app to the 'result' view.
   * 
   * @param {Object} data - The payload from the backend containing base64 images and metadata.
   */

  const applyNextData = (data) => {
      if (!data) return;
      if (data.test_finished) {
        setFinalResults(data.final_thresholds);
        setOverallTime(data.overall_time_sec);
        setView('result');
      } else {
        setTestData(data);
      }
  };

  /**
   * Safely aborts an active test session and returns the patient to the home screen.
   * 
   * @requirements
   * - REQ-F01: Patient Workflow (Abort capability)
   */

  const abortTest = () => {
    setView('home');
    setTestData(null);
  };

  
  const handleLogout = () => {
    clearAuthToken();
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return <LoginScreen onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  if (view === 'test' && testData) {
    return (
        <TestScreen 
          data={testData} 
          onNext={submitAnswer} 
          applyNextData={applyNextData}
          onAbort={abortTest}
        />
    );
  }

  return (
    <div className="app-container">
      {view === 'admin' && (
        <AdminScreen onBack={() => setView('home')} />
      )}
      {view === 'system_admin' && (
        <SystemAdminScreen onBack={() => setView('home')} />
      )}
      
      {view === 'home' && (
        <HomeScreen 
          onLogout={handleLogout}
          onSystemAdmin={() => setView('system_admin')}
          users={users}
          selectedUserId={selectedUserId}
          setSelectedUserId={setSelectedUserId}
          newUserName={newUserName}
          setNewUserName={setNewUserName}
          createUser={createUser}
          testMode={testMode}
          setTestMode={setTestMode}
          testModality={testModality}
          setTestModality={setTestModality}
          testedEye={testedEye}
          setTestedEye={setTestedEye}
          onStart={attemptStartTest}
          onViewHistory={() => setView('history')}
          onRecalibrate={() => setView('calibrate')}
          onAdmin={() => setView('admin')}
          calibrationData={calibrationData}
          onForceTutorial={() => setView('tutorial')}
        />
      )}
      
      {view === 'tutorial' && (
        <TutorialScreen 
          onComplete={handleTutorialComplete}
          onCancel={() => setView('home')}
          testModality={testModality}
        />
      )}
      
      {view === 'calibrate' && (
          <CalibrationScreen onComplete={onCalibrationComplete} initialData={calibrationData} />
      )}
      
      {view === 'result' && (
        <ResultScreen 
          results={finalResults} 
          overallTime={overallTime}
          onBack={() => setView('home')} 
        />
      )}

      {view === 'history' && (
        <HistoryScreen 
          userId={selectedUserId}
          onBack={() => setView('home')}
        />
      )}
    </div>
  );
}

// --- COMPONENT: TEST SCREEN (FREE VIEWING 4-AFC) ---
function TestScreen({ data, onNext, applyNextData, onAbort }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [feedbackState, setFeedbackState] = useState(null); // 'fade-in' | 'fade-out' | null
  const [isCorrectFeedback, setIsCorrectFeedback] = useState(null);
  const [trialStartTime, setTrialStartTime] = useState(Date.now());
  const [currentBlock, setCurrentBlock] = useState(null);
  const [showIntro, setShowIntro] = useState(false);

  const getBlockType = (cond) => {
    if (!cond) return null;
    if (!String(cond).startsWith('color_')) return 'csf';
    if (String(cond) === 'color_90') return 'yellow';
    if (String(cond) === 'color_270') return 'blue';
    return 'unknown';
  };

  useEffect(() => {
    const newBlock = getBlockType(data.current_condition_key);
    if (newBlock !== currentBlock) {
        setCurrentBlock(newBlock);
        setShowIntro(true);
    } else {
        setTrialStartTime(Date.now());
    }
  }, [data.trial_count, data.current_condition_key, currentBlock]);

  /**
   * Handles the patient's selection of a Gabor patch.
   * 
   * Computes the reaction time, determines correctness, displays a brief visual 
   * fade animation (feedback), and delegates the result to the engine. Includes a 
   * state guard (`isProcessing`) acting as a software debounce.
   * 
   * @param {number} index - The index (0-3) of the patch the patient tapped.
   * 
   * @requirements
   * - REQ-U03: Error Prevention (300ms Debounce)
   * - REQ-F06: Touch Registration
   */

  const handlePlateClick = async (index) => {
    if (isProcessing) return;
    setIsProcessing(true);

    const reactionTime = (Date.now() - trialStartTime) / 1000.0;
    const isCorrect = (index === data.target_index);
    const condition_key = data.current_condition_key;

    // Start feedback fade-in over the actual target
    setIsCorrectFeedback(isCorrect);
    setFeedbackState('fade-in');

    // Wait for the fade-in animation (250ms) AND the backend request concurrently
    const fadeTimer = new Promise(resolve => setTimeout(resolve, 250));
    const nextDataPromise = onNext(isCorrect, condition_key, reactionTime);

    const [, nextData] = await Promise.all([fadeTimer, nextDataPromise]);

    // Apply the next data (this changes the images underneath immediately or switches view)
    applyNextData(nextData);

    if (nextData && !nextData.test_finished) {
        // Fade out over the new images (250ms)
        setFeedbackState('fade-out');
        await new Promise(resolve => setTimeout(resolve, 250));
        setFeedbackState(null);
    }
    
    // Clear processing lock
    setIsProcessing(false);
  };

  const isColorTest = currentBlock === 'yellow' || currentBlock === 'blue';

  useEffect(() => {
    if (isColorTest) {
      document.body.classList.add('color-test-mode');
    } else {
      document.body.classList.remove('color-test-mode');
    }
    return () => document.body.classList.remove('color-test-mode');
  }, [isColorTest]);

  const [exampleImage, setExampleImage] = useState(null);

  useEffect(() => {
    if (showIntro) {
      let condKey = currentBlock === 'csf' ? '3.0' : currentBlock === 'yellow' ? 'color_90' : 'color_270';
      if (data && data.condition_key) {
        condKey = data.condition_key;
      }
      const params = new URLSearchParams({
          cond_key: condKey,
          css_px_per_cm: 40,
          viewing_distance_cm: 60,
          device_pixel_ratio: window.devicePixelRatio || 1,
          patch_css_size: 150
      });
      apiFetch(`/api/csf/example?${params.toString()}`)
        .then(res => res.json())
        .then(resData => setExampleImage(resData.image))
        .catch(err => console.error(err));
    }
  }, [showIntro, currentBlock, data]);

  if (showIntro) {
     return (
        <div className="test-screen" style={isColorTest ? { backgroundColor: '#131313', color: '#fff', minHeight: '100vh', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '40px' } : {}}>
          <div className="test-header">
             <h1 style={{ fontSize: '2rem', marginBottom: '20px' }}>
               {currentBlock === 'csf' && 'Contrast Test'}
               {currentBlock === 'yellow' && <span style={{ color: '#FFD700' }}>Color Test: Yellow</span>}
               {currentBlock === 'blue' && <span style={{ color: '#4287f5' }}>Color Test: Blue</span>}
             </h1>
             <p style={{ fontSize: '1.2rem', marginBottom: '40px', opacity: 0.8 }}>
               {currentBlock === 'csf' && 'Select the pattern (striped circle).'}
               {currentBlock === 'yellow' && <>Select the <span style={{ color: '#FFD700', fontWeight: 'bold' }}>yellow</span> circle.</>}
               {currentBlock === 'blue' && <>Select the <span style={{ color: '#4287f5', fontWeight: 'bold' }}>blue</span> circle.</>}
             </p>
             
             <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', marginBottom: '40px', minHeight: '150px' }}>
               {exampleImage ? (
                 <img src={exampleImage} alt="Example Target" style={{ width: '150px', height: '150px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)' }} />
               ) : (
                 <div style={{ width: 150, height: 150, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)' }} />
               )}
             </div>
          </div>
          
          <button className="btn-primary" style={{ padding: '15px 40px', fontSize: '1.2rem', maxWidth: '300px' }} onClick={() => { setShowIntro(false); setTrialStartTime(Date.now()); }}>
            Start Test
          </button>
        </div>
     );
  }


  return (
    <div className="test-screen" style={isColorTest ? { backgroundColor: '#131313', color: '#fff' } : { backgroundColor: '#666666', color: '#fff' }}>
      <div className="test-header">
        <div className="instruction">
          {currentBlock === 'csf' && 'Select the pattern.'}
          {currentBlock === 'yellow' && <>Select the <span style={{ color: '#FFD700', fontWeight: 'bold' }}>yellow</span> circle.</>}
          {currentBlock === 'blue' && <>Select the <span style={{ color: '#4287f5', fontWeight: 'bold' }}>blue</span> circle.</>}
        </div>
        <div className="sub-instruction">
          Free viewing allowed. Guess if you don't know.
        </div>
      </div>

      <div className="grid-container-csf" style={{ maxWidth: data.css_size_px ? `${data.css_size_px * 2 + 10}px` : '600px', backgroundColor: isColorTest ? '#131313' : 'var(--bg-color)' }}>
        {[0, 1, 2, 3].map(i => {
          const isTarget = (i === data.target_index);
          const showFeedback = isTarget && feedbackState !== null;
          let feedbackClass = "";
          if (showFeedback) {
              feedbackClass = isCorrectFeedback ? "feedback-correct" : "feedback-incorrect";
          }

          return (
            <div 
              key={i} 
              className="csf-plate"
              onClick={() => handlePlateClick(i)}
              style={Object.assign({ backgroundColor: isColorTest ? '#131313' : 'var(--bg-color)' }, data.css_size_px ? { width: `${data.css_size_px}px`, height: `${data.css_size_px}px` } : {})}
            >
                <img src={data[`circles_${i}`]} alt="Gabor Patch" style={{ width: data.css_size_px ? `${data.css_size_px}px` : '100%', height: data.css_size_px ? `${data.css_size_px}px` : '100%', display: 'block', transform: 'translateZ(0)' }} />
                {showFeedback && (
                    <div className={`feedback-overlay ${feedbackState} ${feedbackClass}`}></div>
                )}
            </div>
          );
        })}
      </div>

      <div className="progress-bar">
        Progress {Math.min(data.trial_count + 1, data.max_trials)} / {data.max_trials} 
      </div>
      
      <button className="btn-danger" onClick={onAbort} style={{ marginTop: '40px' }}>
        Abort Test
      </button>
    </div>
  );
}

// --- COMPONENT: RESULTS ---
const hueNames = {
    'color_90': 'Yellow',
    'color_270': 'Blue'
};

function formatKey(key) {
    if (hueNames[key]) return hueNames[key];
    if (key.startsWith('color_')) return `Color ${key.split('_')[1]}`;
    return `${key} cpd`;
}

function formatValue(key, val) {
    if (key.startsWith('color_')) return `${(-val).toFixed(2)} logDS`;
    return `${(-val).toFixed(2)} logCS`;
}

function ResultScreen({ results, overallTime, onBack }) {
  return (
    <div className="result-screen">
      <h1>Test Complete</h1>
      {overallTime && (
          <p style={{ opacity: 0.9, color: '#4ade80', fontSize: '1.2rem' }}>
              Test duration: {Math.floor(overallTime / 60)}m {Math.floor(overallTime % 60)}s
          </p>
      )}
      <p style={{ opacity: 0.7 }}>Contrast Sensitivity Thresholds</p>
      
      <div className="results-grid">
          {Object.entries(results).sort(([a], [b]) => {
              const getWeight = (k) => k === 'color_270' ? 1000 : k === 'color_90' ? 1001 : Number(k);
              return getWeight(a) - getWeight(b);
          }).map(([cpd, thresh]) => (
              <div key={cpd} className="result-item">
                  <span className="cpd-label">{formatKey(cpd)}</span>
                  <span className="thresh-val">{formatValue(cpd, thresh)}</span>
              </div>
          ))}
      </div>
      
      <button className="btn-primary" onClick={onBack}>
        Back to Start
      </button>
    </div>
  );
}

// --- COMPONENT: HISTORY ---
/**
 * Fetches and displays a patient's historical test sessions.
 * 
 * @param {Object} props
 * @param {number} props.userId - The ID of the patient to query.
 * @param {Function} props.onBack - Callback to return to the home screen.
 */
function HistoryScreen({ userId, onBack }) {
  const [history, setHistory] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [eyeFilter, setEyeFilter] = useState('all');

  const calculateCombinedScore = (thresholds) => {
      let sumCS = 0;
      let sumDS = 0;
      const cpdKeys = Object.keys(thresholds).filter(k => !k.startsWith('color_'));
      cpdKeys.forEach(k => { sumCS += Number((-thresholds[k]).toFixed(2)); });
      
      if (thresholds['color_90']) sumDS += Number((-thresholds['color_90']).toFixed(2));
      if (thresholds['color_270']) sumDS += Number((-thresholds['color_270']).toFixed(2));
      
      const combined = ((sumCS + sumDS) / 15.6) * 100;
      return Math.min(100, Math.max(0, combined));
  };

  useEffect(() => {
    if (userId) {
      apiFetch(`/api/csf/history?patient_id=${userId}`)
        .then(res => res.json())
        .then(data => {
            setHistory(data);
            if(data.length > 0) setSelectedSessionId(data[data.length-1].id);
        })
        .catch(err => console.error(err));
    }
  }, [userId]);

  const filteredHistory = history.filter(h => eyeFilter === 'all' || h.tested_eye === eyeFilter);
  const selectedSession = filteredHistory.find(h => h.id === selectedSessionId) || (filteredHistory.length > 0 ? filteredHistory[filteredHistory.length - 1] : null);

  const combinedChartData = history.map(h => {
      const d = new Date(h.created_at);
      const score = Number(calculateCombinedScore(h.final_thresholds).toFixed(1));
      return {
          name: d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
          right: h.tested_eye === 'right' ? score : null,
          left: h.tested_eye === 'left' ? score : null,
          both: h.tested_eye === 'both' ? score : null,
          score: score // fallback for single line
      };
  });

  let chartData = [];
  if (selectedSession) {
      const thresholds = selectedSession.final_thresholds;
      const cpdKeys = Object.keys(thresholds).filter(k => !k.startsWith('color_')).sort((a,b) => Number(a) - Number(b));
      chartData = cpdKeys.map(key => ({
          name: key,
          logCS: Number((-thresholds[key]).toFixed(2))
      }));
      if (thresholds['color_270']) {
          chartData.push({ name: 'B', B: Number((-thresholds['color_270']).toFixed(2)) });
      }
      if (thresholds['color_90']) {
          chartData.push({ name: 'Y', Y: Number((-thresholds['color_90']).toFixed(2)) });
      }
  }

  return (
    <div className="history-screen" style={{width: '100%', maxWidth: '600px', marginTop: '40px', textAlign: 'center'}}>
      <h1>Test History</h1>
      
      {!userId ? (
        <p>Please select a user first.</p>
      ) : history.length === 0 ? (
        <p style={{marginTop: '20px', opacity: 0.7}}>No completed tests found.</p>
      ) : (
        <>
          <div className="card" style={{marginTop: '20px'}}>
              <div className="input-group">
                <label>Filter by Tested Eye</label>
                <select 
                    value={eyeFilter} 
                    onChange={e => {
                        setEyeFilter(e.target.value);
                        setSelectedSessionId(null); // Reset selection to auto-select latest
                    }}
                >
                    <option value="all">All Eyes (Combined Overview)</option>
                    <option value="right">Right Eye</option>
                    <option value="left">Left Eye</option>
                    <option value="both">Binocular (Both Eyes)</option>
                </select>
              </div>

              <div className="input-group">
                <label>Select Session</label>
                <select 
                    value={selectedSession ? selectedSession.id : ''} 
                    onChange={e => setSelectedSessionId(Number(e.target.value))}
                >
                    {filteredHistory.map(h => {
                        const d = new Date(h.created_at);
                        const dateStr = d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                        const eyeLabel = h.tested_eye === 'right' ? '(Right)' : h.tested_eye === 'left' ? '(Left)' : '(Binoc)';
                        return (
                            <option key={h.id} value={h.id}>
                                {dateStr} - {h.test_type.toUpperCase()} {eyeLabel}
                            </option>
                        );
                    })}
                </select>
              </div>
          </div>

          <div className="card" style={{height: '300px', width: '100%', padding: '20px 20px 20px 0', marginTop: '20px'}}>
            <h3 style={{marginLeft: '20px', marginBottom: '10px', fontSize: '1.2rem', color: '#ccc'}}>Longitudinal Macular Score (0-100)</h3>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={eyeFilter === 'all' ? combinedChartData : combinedChartData.filter((_, i) => history[i].tested_eye === eyeFilter)} margin={{ top: 5, right: 30, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                <XAxis dataKey="name" stroke="#ccc" tick={{fontSize: 10}} />
                <YAxis stroke="#ccc" domain={[0, 100]} />
                <Tooltip contentStyle={{backgroundColor: '#222', borderColor: '#444', color: '#fff'}} />
                {eyeFilter === 'all' ? (
                  <>
                    <Line type="monotone" dataKey="right" name="Right Eye" stroke="#f87171" strokeWidth={3} connectNulls={true} />
                    <Line type="monotone" dataKey="left" name="Left Eye" stroke="#60a5fa" strokeWidth={3} connectNulls={true} />
                    <Line type="monotone" dataKey="both" name="Binocular" stroke="#a78bfa" strokeWidth={3} connectNulls={true} />
                  </>
                ) : (
                  <Line type="monotone" dataKey="score" name="Combined Score" stroke="#22c55e" strokeWidth={3} connectNulls={true} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {selectedSession && (
              <div className="card" style={{height: '350px', width: '100%', padding: '20px 20px 20px 0', marginTop: '20px'}}>
                <h3 style={{marginLeft: '20px', marginBottom: '10px', fontSize: '1.2rem', color: '#ccc'}}>Selected Session Breakdown</h3>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      stroke="#ccc" 
                      label={{ value: 'Spatial Freq (cpd) / Color', position: 'insideBottom', offset: -15, fill: '#ccc' }} 
                    />
                    <YAxis 
                      stroke="#ccc" 
                      domain={[0, 3]} 
                      label={{ value: 'logCS / logDS', angle: -90, position: 'insideLeft', fill: '#ccc' }}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          return (
                            <div style={{backgroundColor: '#222', border: '1px solid #444', borderRadius: '8px', padding: '10px', color: '#fff'}}>
                              {payload.map((entry, index) => {
                                if (entry.name === 'name' || entry.dataKey === 'name') return null;
                                let displayName = entry.name;
                                if (displayName === 'B') displayName = 'Blue';
                                if (displayName === 'Y') displayName = 'Yellow';
                                return (
                                  <div key={index} style={{ color: entry.color || '#fff', marginBottom: '5px' }}>
                                    {displayName} : {entry.value}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="logCS" 
                      name="Contrast Sensitivity" 
                      stroke="#22c55e" 
                      strokeWidth={3} 
                      activeDot={{ r: 8 }} 
                      dot={{r: 4, fill: '#22c55e'}}
                      connectNulls={false}
                    />
                    <Scatter dataKey="B" name="Blue" fill="#3b82f6" shape="cross" />
                    <Scatter dataKey="Y" name="Yellow" fill="#eab308" shape="triangle" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
          )}
        </>
      )}

      <button className="btn-secondary" onClick={onBack} style={{marginTop: '30px', padding: '12px 30px'}}>
        Back to Home
      </button>
    </div>
  );
}

// --- COMPONENT: CALIBRATION ---
function CalibrationScreen({ onComplete, initialData }) {
  const [step, setStep] = useState(1);
  
  const defaultHeight = initialData ? Math.round(initialData.cssPxPerCm * 8.56) : 400;
  const defaultDistance = initialData ? initialData.viewingDistanceCm : 40;

  const [cardHeightPx, setCardHeightPx] = useState(defaultHeight); 
  const [distance, setDistance] = useState(defaultDistance); 

  const handleFinish = () => {
    // 85.6 mm = 8.56 cm
    const cssPxPerCm = cardHeightPx / 8.56;
    const devicePixelRatio = window.devicePixelRatio || 1;
    
    const calData = {
      cssPxPerCm: cssPxPerCm,
      viewingDistanceCm: distance,
      devicePixelRatio: devicePixelRatio
    };
    
    localStorage.setItem('visionguard_cal', JSON.stringify(calData));
    onComplete(calData);
  };

  if (step === 1) {
    const cardWidthPx = cardHeightPx * (53.98 / 85.6);
    return (
      <div className="calibration-screen">
        <h1>Screen Calibration</h1>
        <p style={{ opacity: 0.8, marginBottom: '20px' }}>
            Please hold a standard credit card vertically against the screen. Adjust the slider until the white box matches exactly.
        </p>
        
        <div className="calibration-area" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '350px' }}>
          <div 
            className="credit-card-box"
            style={{ 
                width: `${cardWidthPx}px`, 
                height: `${cardHeightPx}px`,
                backgroundColor: 'white',
                border: '1px solid red',
                borderRadius: `${cardWidthPx * 0.05}px`, // approximate standard rounded corners relative to width
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
            }}
          ></div>
        </div>
        
        <div className="calibration-controls" style={{ display: 'flex', alignItems: 'center', gap: '15px', padding: '20px', background: 'var(--panel-bg)', borderRadius: '12px', marginBottom: '20px' }}>
          <button className="btn-circle" style={{ fontSize: '1.5rem', width: '50px', height: '50px', borderRadius: '25px' }} onClick={() => setCardHeightPx(prev => Math.max(100, prev - 10))}>-</button>
          <input 
            type="range" 
            min="100" max="800" 
            value={cardHeightPx} 
            onChange={(e) => setCardHeightPx(Number(e.target.value))}
            style={{ flex: 1 }}
          />
          <button className="btn-circle" style={{ fontSize: '1.5rem', width: '50px', height: '50px', borderRadius: '25px' }} onClick={() => setCardHeightPx(prev => Math.min(800, prev + 10))}>+</button>
        </div>
        
        <button className="btn-primary" onClick={() => setStep(2)}>Next</button>
      </div>
    );
  }

  // Step 2
  return (
      <div className="calibration-screen">
        <h1>Viewing Distance</h1>
        <p style={{ opacity: 0.8, marginBottom: '30px' }}>How far are you from the screen?</p>
        
        <div className="distance-presets" style={{ display: 'flex', gap: '10px', marginBottom: '30px' }}>
          <button className="btn-secondary" style={{ flex: 1, padding: '15px' }} onClick={() => setDistance(60)}>PC (60 cm)</button>
          <button className="btn-secondary" style={{ flex: 1, padding: '15px' }} onClick={() => setDistance(40)}>Smartphone (40 cm)</button>
        </div>
        
        <div className="calibration-controls distance-slider" style={{ background: 'var(--panel-bg)', padding: '30px 20px', borderRadius: '12px', marginBottom: '20px' }}>
            <h2 style={{ textAlign: 'center', fontSize: '2rem', margin: '0 0 20px 0', color: 'var(--accent-color)' }}>{distance} cm</h2>
            <input 
              type="range" 
              min="25" max="100" 
              value={distance} 
              onChange={(e) => setDistance(Number(e.target.value))}
              style={{ width: '100%' }}
            />
        </div>
        
        <button className="btn-primary" onClick={handleFinish}>Finish Calibration</button>
      </div>
  );
}

export default App;